import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { NAV, LANDING } from "@/constants/testIds";
import PricingCalculator from "@/components/PricingCalculator";
import { Logo } from "@/components/Logo";
import VSLBanner, { useVSLUnlocked } from "@/components/VSLBanner";
import { getMarket, fmt, bookingFeeFor } from "@/config/markets";
import {
  Mail,
  ArrowRight, CheckCircle2, Zap, LayoutDashboard,
  X as XIcon, Minus, Trophy, Info,
} from "lucide-react";

/**
 * Market-aware landing page.
 *
 * @param {"uk"|"us"} marketKey - which region's currency & copy to render
 * @param {boolean}   showVSL   - whether to render the top VSL banner + gate
 */
export default function Landing({ marketKey = "uk", showVSL = true }) {
  const market = getMarket(marketKey);
  const [unlocked, unlock] = useVSLUnlocked(marketKey);

  // Remember market so downstream pages (e.g. About) know which currency to show.
  useEffect(() => {
    try { localStorage.setItem("st_market", marketKey); } catch (e) { /* ignore */ }
  }, [marketKey]);

  // Effectively unlock when there's no VSL gate to satisfy
  const gated = showVSL && !unlocked;

  useEffect(() => {
    if (gated) window.scrollTo({ top: 0, behavior: "auto" });
  }, [gated]);

  const feePct = Math.round(market.bookingFeePercent * 100);
  const feeMinStr = fmt(market, market.bookingFeeMinimum, { decimals: 2 });
  const feeSummary = `${feePct}% booking fee per ticket (${feeMinStr} minimum)`;
  // Founding Partner "Switch To Us" concierge credit — headline number in the
  // buyer's local currency. £500 in the UK, $500 in the US (same symbol swap).
  const switchCreditStr = `${market.symbol}500`;

  const s = market.savings;

  // Comparison scenario — volume selector for the "You Keep / Buyer Pays" row.
  // Options in ticket units (per year).
  const [scenarioVolume, setScenarioVolume] = useState(10000);
  const scenarioOptions = [
    { value: 1000,  label: "1k"  },
    { value: 5000,  label: "5k"  },
    { value: 10000, label: "10k" },
    { value: 50000, label: "50k" },
  ];
  const scenarioTicketPrice = 40; // Fixed £40 face-value benchmark

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      {showVSL && <VSLBanner videoUrl={market.vslUrl} onUnlock={unlock} storageKey={`vsl_watched_${market.key}`} />}

      <div
        className={`transition-[filter,opacity] duration-500 ${gated ? "vsl-gate-locked" : ""}`}
        aria-hidden={gated}
        data-testid="landing-body"
      >
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-32 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid={NAV.logo}>
            <Logo size="h-28" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <a href="#comparison" className="text-zinc-700 hover:text-zinc-900">Compare</a>
            <a href="#revenue-share" className="text-zinc-700 hover:text-zinc-900">Rewards</a>
            <a href="#pricing" className="text-zinc-700 hover:text-zinc-900">Pricing</a>
            <Link to="/about" className="text-zinc-700 hover:text-zinc-900" data-testid="nav-about-link">About</Link>
            <a href="#faq" className="text-zinc-700 hover:text-zinc-900">FAQ</a>
            <a href="#contact" className="text-zinc-700 hover:text-zinc-900" data-testid="nav-contact-link">Contact</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" data-testid={NAV.loginBtn} className="rounded-full">Log in</Button></Link>
            <Link to="/register">
              <Button data-testid={NAV.signupBtn} className="rounded-full bg-zinc-900 hover:bg-zinc-800">
                Start selling <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-24 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-200 bg-white text-xs font-medium text-zinc-700 mb-6">
                <Zap className="w-3.5 h-3.5 text-indigo-600" />
                Your all-in-one event platform · Tickets, check-in, email &amp; more
              </div>
              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tighter">
                Everything Your<br />
                Event Needs. <span className="text-indigo-600">In One Place</span>.
              </h1>
              <p className="mt-6 font-display font-bold text-2xl lg:text-3xl tracking-tight text-zinc-900">
                Sell tickets. Check in guests. Send the emails. Print the list. Done.
              </p>
              <p className="mt-4 text-lg text-zinc-600 max-w-xl">
                The all-in-one event platform for venues, promoters and event owners — with the best commercials in the industry.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register">
                  <Button size="lg" data-testid={LANDING.heroCta} className="rounded-full bg-zinc-900 hover:bg-zinc-800 h-12 px-6 text-base">
                    Start free — 5 minutes <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href="#comparison" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="comparison-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" }); }}>
                  <Button size="lg" variant="outline" data-testid={LANDING.heroSecondaryCta} className="rounded-full h-12 px-6 text-base border-zinc-300">
                    See the numbers
                  </Button>
                </a>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-zinc-500">
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> No card required</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Cancel anytime</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Live in 10 minutes</div>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden border border-zinc-200 shadow-xl aspect-[4/5]">
                <img src="https://images.pexels.com/photos/5193526/pexels-photo-5193526.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Live event crowd" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <div className="text-xs uppercase tracking-widest opacity-80">Powered by Affra</div>
                  <div className="mt-1 font-display font-black text-2xl leading-tight">Northern Nights · Fri 8pm</div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="px-2.5 py-1 bg-white text-black rounded-md text-xs font-semibold">{fmt(market, 12.50, { decimals: 2 })}</span>
                    <span className="px-2.5 py-1 bg-white/20 backdrop-blur text-white rounded-md text-xs font-semibold border border-white/30">Sold out warning</span>
                  </div>
                </div>
              </div>
              <div className="hidden lg:block absolute -left-16 bottom-8 bg-white border border-zinc-200 rounded-xl p-4 w-64 shadow-lg">
                <div className="flex items-center gap-2 text-xs font-medium text-zinc-500">
                  <LayoutDashboard className="w-3.5 h-3.5" /> This week
                </div>
                <div className="mt-2 font-display font-black text-3xl">1,204</div>
                <div className="text-xs text-zinc-500">tickets sold across 3 events</div>
                <div className="mt-3 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                  <div className="h-full w-3/4 bg-indigo-600" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* COMPARISON — Named competitors vs Affra */}
      <section
        id="comparison"
        data-testid="comparison-section"
        className="border-t border-zinc-200 bg-white"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-2xl mb-12">
            <div className="text-sm font-mono text-indigo-600 uppercase tracking-widest">/ them vs us</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tighter">
              Priced up against the big names.<br />
              <span className="text-indigo-600">Only Affra earns green ticks all the way down.</span>
            </h2>
            <p className="mt-4 text-lg text-zinc-600">
              A fair, feature-by-feature look at Eventbrite, Ticket Tailor and Skiddle — the three platforms UK &amp; US venues most often move to Affra from.
            </p>
          </div>

          {(() => {
            // Cell states: "yes" | "partial" | "no"
            const cell = (state) => {
              if (state === "yes") return (
                <span className="w-8 h-8 rounded-full bg-emerald-600 text-white grid place-items-center" aria-label="Included">
                  <CheckCircle2 className="w-4 h-4" strokeWidth={2.5} />
                </span>
              );
              if (state === "partial") return (
                <span className="w-8 h-8 rounded-full bg-amber-50 text-amber-600 border border-amber-200 grid place-items-center" aria-label="Partial / paid add-on">
                  <Minus className="w-4 h-4" strokeWidth={3} />
                </span>
              );
              return (
                <span className="w-8 h-8 rounded-full bg-rose-50 text-rose-600 grid place-items-center" aria-label="Not included">
                  <XIcon className="w-4 h-4" strokeWidth={3} />
                </span>
              );
            };

            const cols = [
              {
                key: "eventbrite",
                name: "Eventbrite",
                tag: "US · Global",
                fee: "6.95% + £1.79",
                feeNote: "Premium tier · highest published rate",
                sourceUrl: "https://www.eventbrite.co.uk/organizer/pricing/",
                sourceLabel: "eventbrite.co.uk/organizer/pricing",
                sourceQuote: "Premium plan: 6.95% + £1.79 service fee per ticket sold — Eventbrite's highest published rate before custom / enterprise pricing. Payment processing bundled into that percentage.",
                youKeepPerTicket: 40,     // buyer-pays-fee model, venue nets face value
                buyerPaysEach: 44.57,     // 40 + (0.0695×40 + 1.79) = 40 + 4.57
                scenarioNote: "At Premium rates your buyer pays £4.57 extra per ticket — an <strong>+11.4% mark-up</strong> at checkout.",
              },
              {
                key: "ticket-tailor",
                name: "Ticket Tailor",
                tag: "UK · Global",
                fee: "1.55% + £0.33",
                feeNote: "Pay-As-You-Go · + Stripe 1.5–2.9% + 25p",
                sourceUrl: "https://www.tickettailor.com/pricing/",
                sourceLabel: "tickettailor.com/pricing",
                sourceQuote: "Pay-As-You-Go: 1.55% + £0.33 per ticket sold — the highest per-ticket rate on the Ticket Tailor pricing page. Payment processing is charged separately by Stripe (typically 1.5% for UK cards, up to 2.9% for Amex / international, + 25p per transaction).",
                // £0.95 platform + £0.85 Stripe = £1.80 comes out of the venue's pocket per ticket
                youKeepPerTicket: 38.20,
                buyerPaysEach: 40,
                scenarioNote: "At PAYG rates <strong>£1.80 per ticket comes straight out of your bank</strong> — platform + Stripe, paid direct by you.",
              },
              {
                key: "skiddle",
                name: "Skiddle",
                tag: "UK",
                fee: "up to ~12%",
                feeNote: "High-demand / premium events",
                sourceUrl: "https://www.skiddle.com/promotion-centre/",
                sourceLabel: "skiddle.com/promotion-centre",
                sourceQuote: "Skiddle charges customers a booking fee that scales with event demand — typically 9–10% on regular events and up to ~12% on high-demand / premium listings. The fee is added on top of the ticket price at checkout. Organisers keep 100% of the face value.",
                youKeepPerTicket: 40,
                buyerPaysEach: 44.80,
                scenarioNote: "At their top rate your buyer pays <strong>£4.80 extra per ticket</strong> — an eye-watering +12% at the checkout.",
              },
              {
                key: "affra",
                name: "Affra",
                tag: "You are here",
                fee: "5% (£1.25 min)",
                feeNote: "Buyer-paid · you keep 100% + Ticket Reward",
                sourceUrl: "/#pricing",
                sourceLabel: "affra.io — pricing",
                sourceQuote: "5% of face value with a £1.25 minimum, charged to the buyer. Stripe card processing absorbed by Affra. You keep 100% of face value on every ticket, plus a £0.10 Ticket Reward paid back per ticket sold — rising to £0.25 for our first 20 Founding Partners.",
                // Face value + 10p Ticket Reward on every ticket
                youKeepPerTicket: 40.10,
                // Founding Partner (first 20 venues) — face value + 25p Ticket Reward
                foundingPartnerPerTicket: 40.25,
                buyerPaysEach: 42.00,
                scenarioNote: "You keep 100% of face value <strong>plus 10p per ticket back</strong> — 25p if you&rsquo;re one of the first 20 Founding Partners. Buyer pays only £2 extra per ticket. Stripe absorbed.",
                winner: true,
              },
            ];

            const fmtMoney = (n) => `£${n.toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
            const fmtMoney2 = (n) => `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

            const rows = [
              {
                label: "100% of face value paid to you",
                note: "Rivals typically deduct a % of every ticket at source",
                states: { "eventbrite": "no", "ticket-tailor": "no", "skiddle": "no", "affra": "yes" },
              },
              {
                label: "Card processing &amp; payout fees absorbed by the platform",
                note: "Stripe / Visa / Mastercard covered out of the buyer's booking fee — nothing on your Stripe statement",
                states: { "eventbrite": "no", "ticket-tailor": "no", "skiddle": "partial", "affra": "yes" },
              },
              {
                label: "Digital QR check-in included",
                note: "Scan on any phone at the door — no extra hardware",
                states: { "eventbrite": "yes", "ticket-tailor": "yes", "skiddle": "yes", "affra": "yes" },
              },
              {
                label: "Email marketing built in",
                note: "Compose &amp; send campaigns to your buyers, from your domain",
                states: { "eventbrite": "partial", "ticket-tailor": "no", "skiddle": "no", "affra": "yes" },
              },
              {
                label: "Guest lists with export &amp; print",
                note: "CSV export &amp; printable door-list PDF included",
                states: { "eventbrite": "yes", "ticket-tailor": "yes", "skiddle": "partial", "affra": "yes" },
              },
              {
                label: "Full breakdown of what the platform makes per ticket",
                note: "You see exactly what we earn on every sale",
                states: { "eventbrite": "no", "ticket-tailor": "partial", "skiddle": "no", "affra": "yes" },
              },
              {
                label: "Revenue share back once you scale",
                note: "Partner Dividends — up to 2.5% of platform fees returned yearly",
                states: { "eventbrite": "no", "ticket-tailor": "no", "skiddle": "no", "affra": "yes" },
              },
              {
                label: "White-label buyer experience",
                note: "Your logo, colours &amp; domain — never their marketplace branding",
                states: { "eventbrite": "no", "ticket-tailor": "partial", "skiddle": "no", "affra": "yes" },
              },
            ];

            return (
              <TooltipProvider delayDuration={120}>
                <div className="rounded-2xl border border-zinc-200 overflow-hidden shadow-sm overflow-x-auto">
                  <div className="min-w-[720px]">
                    {/* Header row */}
                    <div className="grid grid-cols-12 bg-zinc-900 text-white text-xs font-mono uppercase tracking-widest">
                      <div className="col-span-4 px-6 py-4">/ feature</div>
                      {cols.map((c) => (
                        <div
                          key={c.key}
                          data-testid={`comparison-col-${c.key}`}
                          className={`col-span-2 px-2 py-4 text-center border-l ${c.winner ? "bg-indigo-600 border-indigo-500" : "border-white/10"}`}
                        >
                          <div className="text-[13px] font-bold tracking-tight normal-case">{c.name}</div>
                          <div className={`text-[9px] mt-0.5 ${c.winner ? "text-indigo-200" : "text-zinc-400"}`}>{c.tag}</div>
                        </div>
                      ))}
                    </div>

                    {/* Scenario row — volume × £40 worked example, all-in incl. payment processing */}
                    <div className="grid grid-cols-12 bg-gradient-to-r from-indigo-50 via-white to-indigo-50 border-b-2 border-indigo-200" data-testid="comparison-fees-row">
                      <div className="col-span-4 px-6 py-6 bg-indigo-600 text-white relative overflow-hidden">
                        <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-white/5" aria-hidden="true" />
                        <div className="relative">
                          <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-200">/ real numbers</div>
                          <div className="mt-1 font-display font-black text-2xl tracking-tighter leading-tight">
                            {scenarioVolume.toLocaleString("en-GB")} tickets<br />
                            <span className="text-indigo-300">at £{scenarioTicketPrice} face value</span>
                          </div>

                          {/* Volume toggle */}
                          <div className="mt-4" role="tablist" aria-label="Ticket volume selector" data-testid="scenario-volume-toggle">
                            <div className="inline-flex rounded-full bg-indigo-900/40 border border-white/15 p-1">
                              {scenarioOptions.map((o) => (
                                <button
                                  key={o.value}
                                  type="button"
                                  role="tab"
                                  aria-selected={scenarioVolume === o.value}
                                  data-testid={`scenario-volume-${o.value}`}
                                  onClick={() => setScenarioVolume(o.value)}
                                  className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest transition-colors ${
                                    scenarioVolume === o.value
                                      ? "bg-white text-indigo-700 shadow"
                                      : "text-indigo-100 hover:text-white"
                                  }`}
                                >
                                  {o.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="mt-3 text-[12px] text-indigo-100 leading-relaxed">
                            Each competitor at their <strong className="text-white">highest published price point</strong>, all-in with payment processing. Hover any figure for the source.
                          </div>
                          <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/20 text-[10px] font-mono uppercase tracking-widest">
                            <Zap className="w-3 h-3" /> Gross {fmtMoney(scenarioVolume * scenarioTicketPrice)}
                          </div>
                        </div>
                      </div>
                      {cols.map((c) => {
                        const youKeep = Math.round(c.youKeepPerTicket * scenarioVolume);
                        const foundingKeep = c.foundingPartnerPerTicket
                          ? Math.round(c.foundingPartnerPerTicket * scenarioVolume)
                          : null;
                        return (
                          <div
                            key={c.key}
                            data-testid={`comparison-fee-${c.key}`}
                            className={`col-span-2 px-2 py-6 text-center border-l ${c.winner ? "bg-indigo-50 border-indigo-100" : "border-zinc-200"}`}
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  data-testid={`comparison-fee-trigger-${c.key}`}
                                  className="group inline-flex flex-col items-center gap-1 cursor-help outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 rounded-md"
                                  aria-label={`View source for ${c.name} fee`}
                                >
                                  <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">You keep</div>
                                  {foundingKeep ? (
                                    <>
                                      {/* Founding partner featured amount */}
                                      <div className="inline-flex items-center gap-1">
                                        <span className={`font-display font-black text-xl lg:text-2xl tracking-tighter leading-none text-indigo-700`}>
                                          {fmtMoney(foundingKeep)}
                                        </span>
                                        <Info className="w-3 h-3 opacity-40 group-hover:opacity-100 transition-opacity text-indigo-700" />
                                      </div>
                                      <div className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-mono uppercase tracking-widest">
                                        <Trophy className="w-2.5 h-2.5" /> Founding Partner
                                      </div>
                                      <div className="mt-1 text-[10px] text-zinc-600">
                                        or <span className="font-semibold text-zinc-900">{fmtMoney(youKeep)}</span> standard
                                      </div>
                                    </>
                                  ) : (
                                    <div className="inline-flex items-center gap-1">
                                      <span className={`font-display font-black text-xl lg:text-2xl tracking-tighter leading-none ${c.youKeepPerTicket === scenarioTicketPrice ? "text-emerald-700" : "text-rose-700"}`}>
                                        {fmtMoney(youKeep)}
                                      </span>
                                      <Info className="w-3 h-3 opacity-40 group-hover:opacity-100 transition-opacity text-zinc-500" />
                                    </div>
                                  )}
                                  <div className="mt-2 pt-2 border-t border-zinc-200/70 w-full">
                                    <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">Buyer pays</div>
                                    <div className={`text-[13px] font-semibold ${c.winner ? "text-indigo-700" : "text-zinc-900"}`}>
                                      {fmtMoney2(c.buyerPaysEach)}<span className="text-[10px] text-zinc-500"> / ticket</span>
                                    </div>
                                  </div>
                                  <div className="mt-1 text-[10px] text-zinc-500 leading-tight max-w-[140px]">{c.fee}</div>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent
                                side="bottom"
                                sideOffset={8}
                                data-testid={`comparison-fee-tooltip-${c.key}`}
                                className="max-w-sm bg-zinc-900 text-white border border-zinc-800 px-4 py-3 text-left shadow-xl"
                              >
                                <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-300 mb-1.5">
                                  {c.name} · {scenarioVolume.toLocaleString("en-GB")} × £{scenarioTicketPrice}
                                </div>
                                <div className="grid grid-cols-2 gap-2 mb-3 text-[12px]">
                                  <div>
                                    <div className="text-zinc-400 text-[10px]">You keep {foundingKeep ? "(FP)" : ""}</div>
                                    <div className="font-display font-bold text-white text-lg">{fmtMoney(foundingKeep || youKeep)}</div>
                                    {foundingKeep && (
                                      <div className="text-[10px] text-zinc-400 mt-0.5">Standard: {fmtMoney(youKeep)}</div>
                                    )}
                                  </div>
                                  <div>
                                    <div className="text-zinc-400 text-[10px]">Buyer pays</div>
                                    <div className="font-display font-bold text-white text-lg">
                                      {fmtMoney2(c.buyerPaysEach)}
                                      <span className="text-[10px] font-normal">/ticket</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="text-[11px] leading-relaxed text-zinc-200 mb-2" dangerouslySetInnerHTML={{ __html: c.scenarioNote }} />
                                <div className="text-[10px] leading-relaxed text-zinc-400 border-t border-zinc-800 pt-2 mt-2">
                                  Headline fee: <span className="text-zinc-200">{c.fee}</span>. {c.sourceQuote}
                                </div>
                                <a
                                  href={c.sourceUrl}
                                  target={c.sourceUrl.startsWith("http") ? "_blank" : undefined}
                                  rel={c.sourceUrl.startsWith("http") ? "noopener noreferrer" : undefined}
                                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-mono text-indigo-300 hover:text-indigo-200 underline underline-offset-2"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {c.sourceLabel} <ArrowRight className="w-3 h-3" />
                                </a>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        );
                      })}
                    </div>

                    {rows.map((row, idx) => (
                      <div
                        key={row.label}
                        data-testid={`comparison-row-${idx}`}
                        className={`grid grid-cols-12 items-center bg-white hover:bg-zinc-50 transition-colors ${idx < rows.length - 1 ? "border-b border-zinc-200" : ""}`}
                      >
                        <div className="col-span-4 px-6 py-5">
                          <div className="font-semibold text-zinc-900 text-[14px] leading-snug" dangerouslySetInnerHTML={{ __html: row.label }} />
                          <div className="text-[11px] text-zinc-500 mt-1 hidden lg:block" dangerouslySetInnerHTML={{ __html: row.note }} />
                        </div>
                        {cols.map((c) => (
                          <div
                            key={c.key}
                            className={`col-span-2 px-2 py-5 flex justify-center border-l ${c.winner ? "bg-indigo-50/40 border-indigo-100" : "border-zinc-100"}`}
                          >
                            {cell(row.states[c.key])}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Legend */}
                <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-zinc-600">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white grid place-items-center">
                      <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
                    </span>
                    Included as standard
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 grid place-items-center">
                      <Minus className="w-3 h-3" strokeWidth={3} />
                    </span>
                    Partial · paid add-on · limited
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-rose-50 text-rose-600 grid place-items-center">
                      <XIcon className="w-3 h-3" strokeWidth={3} />
                    </span>
                    Not offered
                  </div>
                  <div className="flex items-center gap-2">
                    <Info className="w-3.5 h-3.5 text-zinc-400" />
                    Hover any fee for the exact source
                  </div>
                </div>

                <div className="mt-4 text-xs text-zinc-500 max-w-3xl">
                  Comparison based on publicly-available pricing and product pages as of {new Date().getFullYear()}. Features can move between competitor tiers — most rivals lock check-in, email or exports behind their higher-priced &quot;Pro&quot; or &quot;Plus&quot; plans. If any of this looks out of date, email <a href="mailto:hello@affra.io" className="underline hover:text-zinc-900">hello@affra.io</a> and we&apos;ll update it.
                </div>
              </TooltipProvider>
            );
          })()}
        </div>
      </section>

      {/* NO SURPRISE FEES — All-in checkout pricing */}
      <section
        id="no-surprises"
        data-testid="no-surprises-section"
        className="border-t border-zinc-200 bg-white"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-3xl">
            <div className="text-sm font-mono text-indigo-600 uppercase tracking-widest">/ honest checkout</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tighter">
              No surprise booking fees.<br />
              <span className="text-indigo-600">Your customers always see the all-in price.</span>
            </h2>
            <p className="mt-4 text-lg text-zinc-600 max-w-2xl">
              Every platform quotes a &quot;from&quot; price and then springs a booking fee on the final screen. We don&apos;t. What your buyer sees at the top of the page is what their card gets charged — no gotchas, no cart abandonment, no angry emails.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-10 items-start">
            {/* THEM — surprise fee receipt */}
            <div data-testid="checkout-them" className="relative">
              <div className="absolute -top-3 left-6 z-10">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-600 text-white text-[10px] font-mono uppercase tracking-widest shadow-md">
                  <XIcon className="w-3 h-3" strokeWidth={3} /> Them
                </span>
              </div>
              <div className="rounded-2xl border-2 border-rose-200 bg-rose-50/40 p-6 pt-8 shadow-sm">
                <div className="rounded-xl bg-white border border-zinc-200 overflow-hidden">
                  {/* Product line */}
                  <div className="px-5 py-4 border-b border-zinc-100">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Event page · Legacy platform</div>
                    <div className="mt-2 flex items-baseline justify-between">
                      <div className="font-semibold text-zinc-900">Northern Nights · Fri 8pm</div>
                      <div className="font-display font-black text-2xl tracking-tighter text-zinc-900">£12.50</div>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">General admission</div>
                  </div>
                  {/* Cart summary — surprise fees appear */}
                  <div className="px-5 py-4 bg-zinc-50 space-y-2 text-[13px]">
                    <div className="flex justify-between text-zinc-700">
                      <span>Ticket</span>
                      <span className="line-through text-zinc-400">£12.50</span>
                    </div>
                    <div className="flex justify-between text-rose-700 relative">
                      <span className="inline-flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        Service fee
                      </span>
                      <span className="font-semibold">+ £1.87</span>
                    </div>
                    <div className="flex justify-between text-rose-700">
                      <span className="inline-flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        Payment processing
                      </span>
                      <span className="font-semibold">+ £0.42</span>
                    </div>
                    <div className="pt-2 mt-2 border-t border-zinc-200 flex justify-between items-baseline">
                      <span className="font-semibold text-zinc-900">Total charged</span>
                      <span className="font-display font-black text-xl tracking-tight text-rose-700">£14.79</span>
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex items-start gap-2 text-sm text-rose-800">
                  <XIcon className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={3} />
                  <div>
                    <div className="font-semibold">+18% more than the listed price.</div>
                    <div className="text-rose-700/80 text-[13px] mt-0.5">Fees revealed at the last click. Cart abandonment climbs. Support tickets pile in.</div>
                  </div>
                </div>
              </div>
            </div>

            {/* US — honest all-in receipt */}
            <div data-testid="checkout-us" className="relative">
              <div className="absolute -top-3 left-6 z-10">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-600 text-white text-[10px] font-mono uppercase tracking-widest shadow-md">
                  <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} /> Affra
                </span>
              </div>
              <div className="rounded-2xl border-2 border-indigo-200 bg-indigo-50/30 p-6 pt-8 shadow-sm">
                <div className="rounded-xl bg-white border border-zinc-200 overflow-hidden">
                  <div className="px-5 py-4 border-b border-zinc-100">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-600">Event page · Affra</div>
                    <div className="mt-2 flex items-baseline justify-between">
                      <div className="font-semibold text-zinc-900">Northern Nights · Fri 8pm</div>
                      <div className="text-right">
                        <div className="font-display font-black text-2xl tracking-tighter text-zinc-900">£13.75</div>
                        <div className="text-[10px] text-zinc-500 mt-0.5">All-in · this is what you pay</div>
                      </div>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">General admission</div>
                  </div>
                  <div className="px-5 py-4 bg-emerald-50/60 space-y-2 text-[13px]">
                    <div className="flex justify-between text-zinc-700">
                      <span>Ticket face value</span>
                      <span>£12.50</span>
                    </div>
                    <div className="flex justify-between text-zinc-600">
                      <span>Booking fee (already shown above)</span>
                      <span>£1.25</span>
                    </div>
                    <div className="pt-2 mt-2 border-t border-emerald-200 flex justify-between items-baseline">
                      <span className="font-semibold text-zinc-900">Total charged</span>
                      <span className="font-display font-black text-xl tracking-tight text-emerald-700">£13.75</span>
                    </div>
                    <div className="pt-1 text-[11px] text-emerald-700 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
                      Same number as the top of the page. Every time.
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex items-start gap-2 text-sm text-indigo-800">
                  <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={2.5} />
                  <div>
                    <div className="font-semibold">Exactly the price they saw. No surprises.</div>
                    <div className="text-indigo-700/80 text-[13px] mt-0.5">Buyers finish checkout. Refund requests drop. Trust in your brand goes up.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Value bullets */}
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              {
                title: "One price. Displayed everywhere.",
                body: "The all-in price appears on the event page, in social share cards, in confirmation emails and on the receipt. No maths for the buyer.",
              },
              {
                title: "Fewer abandoned carts.",
                body: "Sticker shock at the final screen is the #1 reason buyers drop out. When the price never changes, they keep going.",
              },
              {
                title: "Zero support tickets about &ldquo;hidden fees&rdquo;.",
                body: "Your inbox stops filling up with &ldquo;why did you charge me extra?&rdquo; because you didn&apos;t.",
              },
            ].map((v, i) => (
              <div key={i} data-testid={`no-surprises-benefit-${i}`} className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-6">
                <div className="w-8 h-8 rounded-lg bg-indigo-600 text-white grid place-items-center">
                  <CheckCircle2 className="w-4 h-4" strokeWidth={2.5} />
                </div>
                <div className="mt-4 font-display font-bold text-lg tracking-tight" dangerouslySetInnerHTML={{ __html: v.title }} />
                <div className="mt-2 text-sm text-zinc-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: v.body }} />
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* REVENUE SHARE LADDER — Partner Dividends tiers */}
      <section
        id="revenue-share"
        data-testid="revenue-share-section"
        className="border-y border-zinc-200 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-5">
              <div className="text-sm font-mono text-indigo-600 uppercase tracking-widest">/ partner dividends</div>
              <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[0.95]">
                Sell more.<br />
                <span className="text-indigo-600">Earn a slice back.</span>
              </h2>
              <p className="mt-6 text-lg text-zinc-600 leading-relaxed">
                Every ticket sold on Affra counts toward your partner tier. Cross a threshold and we start paying you back a percentage of the booking fees we&apos;ve earned — automatically, every year.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-zinc-600">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> No form to fill in. No opt-in required.</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Tier resets never — your lifetime ticket count only goes up.</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Dividends paid straight to your Stripe balance.</li>
              </ul>
            </div>

            <div className="lg:col-span-7 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { key: "bronze",   label: "Bronze",   range: "0 – 5k tickets",     rate: "1.0%", accent: "from-amber-700 to-amber-900",   text: "text-amber-100",  ring: "ring-amber-200/50",  bg: "bg-amber-50",  offset: "translate-y-4",   badge: "Starter" },
                { key: "silver",   label: "Silver",   range: "5k – 10k tickets",   rate: "1.5%", accent: "from-zinc-400 to-zinc-600",     text: "text-zinc-100",   ring: "ring-zinc-200/50",   bg: "bg-zinc-100",  offset: "translate-y-2",   badge: "Growing" },
                { key: "gold",     label: "Gold",     range: "10k – 15k tickets",  rate: "2.0%", accent: "from-yellow-500 to-amber-600",  text: "text-amber-50",   ring: "ring-yellow-200/60", bg: "bg-yellow-50", offset: "translate-y-0",   badge: "Scaling" },
                { key: "platinum", label: "Platinum", range: "15k+ tickets",       rate: "2.5%", accent: "from-indigo-600 to-indigo-900", text: "text-indigo-100", ring: "ring-indigo-200/60", bg: "bg-indigo-50", offset: "-translate-y-2",  badge: "Top tier" },
              ].map((t) => (
                <div
                  key={t.key}
                  data-testid={`tier-card-${t.key}`}
                  className={`relative rounded-2xl overflow-hidden border border-zinc-200 shadow-sm ${t.bg} transform ${t.offset} transition-transform hover:-translate-y-3`}
                >
                  <div className={`bg-gradient-to-br ${t.accent} px-4 pt-5 pb-8 ${t.text}`}>
                    <div className="flex items-center justify-between">
                      <Trophy className="w-4 h-4 opacity-80" />
                      <span className="text-[10px] font-mono uppercase tracking-widest opacity-80">{t.badge}</span>
                    </div>
                    <div className="mt-3 font-display font-black text-2xl tracking-tight">{t.label}</div>
                    <div className="text-[11px] opacity-80 mt-0.5">{t.range}</div>
                  </div>
                  <div className="px-4 py-4">
                    <div className="text-[10px] font-mono uppercase text-zinc-500 tracking-widest">Yearly dividend</div>
                    <div className="mt-1 font-display font-black text-4xl tracking-tighter text-zinc-900">{t.rate}</div>
                    <div className="mt-1 text-[11px] text-zinc-500">of platform fees earned</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 text-xs text-zinc-500 max-w-3xl">
            Partner Dividends are calculated on the Affra platform fees you generate in a given year. Tier assignment is based on your lifetime ticket count. Payments issued in the January following each qualifying year to the active Stripe account on file.
          </div>
        </div>
      </section>

      {/* PRICING CARD */}
      <section id="pricing" className="relative bg-zinc-900 text-white overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-[0.04]" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-6">
              <div className="text-sm font-mono text-indigo-400 uppercase tracking-widest">/ pricing</div>
              <h2 className="mt-3 font-display font-black text-4xl lg:text-6xl tracking-tighter leading-none">
                Free forever.<br />Simple, transparent pricing.
              </h2>
              <p className="mt-6 text-zinc-400 text-lg max-w-md">
                No monthly fees. No cost per ticket. We only get paid when you sell tickets — through the buyer&apos;s {feeSummary}. Out of that we cover <strong className="text-white">Stripe card processing on every sale</strong> and you keep <strong className="text-white">100% of every ticket price</strong>. No card fees on your Stripe statement, no reconciliation.
              </p>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-2xl bg-white text-zinc-900 p-10 border border-white/10">
                <div className="flex items-baseline gap-2">
                  <span className="font-display font-black text-6xl tracking-tighter">{market.symbol}0</span>
                  <span className="text-zinc-500">/ month · free forever</span>
                </div>
                <div className="mt-2 text-sm text-zinc-500">
                  {`We only get paid when you sell tickets — via the buyer's ${feePct}% booking fee (${feeMinStr} minimum) per ticket.`}
                </div>
                <ul className="mt-8 space-y-3 text-[15px]">
                  {[
                    "No monthly fees — free forever",
                    "No per-ticket cost to you",
                    "Unlimited events, tickets & buyers",
                    `${feePct}% booking fee per ticket (${feeMinStr} minimum) — paid by the buyer`,
                    "We absorb Stripe card processing on every sale",
                    "Per-ticket pricing — not per transaction",
                    "Fully white-label event pages",
                    "Custom subdomain (yourvenue.affra.io)",
                    "Email from your own domain",
                    "Direct Stripe payouts",
                    "One-off & recurring events",
                    "Digital QR tickets",
                    "Digital QR scanner built in",
                    "Digital QR membership cards",
                    "Live analytics & attendee export",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register" className="block mt-8">
                  <Button data-testid={LANDING.pricingCta} className="w-full h-12 rounded-full bg-zinc-900 hover:bg-zinc-800 text-base">
                    Get started free <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-sm font-mono text-indigo-600 uppercase tracking-widest">/ faq</div>
        <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tighter">Questions? We got you.</h2>
        <div className="mt-10 divide-y divide-zinc-200 border-y border-zinc-200">
          {[
            ["Are you open to promoters as well as venues?", `Yes — both. **Venues** run events at one fixed location (their own room). **Promoters** run events across many locations — clubs, warehouses, tours, festivals — and set the location per event. The pricing is identical: a ${feePct}% booking fee (${feeMinStr} minimum) charged to the buyer, no monthly fee, and we absorb Stripe card processing on every sale. Pick your account type on the sign-up page.`],
            ["How does the booking fee work?", `Ticket buyers pay a small booking fee at checkout — ${feePct}% of face value with a ${feeMinStr} minimum. That's how Affra gets paid. Out of that fee, we pay Stripe's card processing costs on your behalf on **every** ticket you sell. You keep 100% of the face value on every ticket, forever. Nothing to reconcile, no card fees on your Stripe statement.`],
            ["We only sell tickets to members, is that a problem?", "Not at all — sell memberships directly through our platform. Your members get a beautiful digital QR members card that lives in their Apple or Google Wallet, so no more replacing lost cards."],
            ["Is there a maximum ticket price?", `No — sell tickets at any price. Our booking fee scales with face value (${feePct}% of face value, ${feeMinStr} minimum) so the maths stays predictable at every price point.`],
            ["Do I need my own Stripe account?", "Yes. You'll connect a Stripe Express account in one click from your dashboard. All ticket revenue is paid directly to you by Stripe on their normal payout schedule."],
            ["Can I use my own domain?", "By default you get a yourvenue.affra.io subdomain. A fully custom domain (tickets.yourvenue.com) is available on request."],
            ["What about recurring events?", "Set it up once with a weekly, monthly or custom schedule and we handle the ticket links, page and analytics for every occurrence."],
            ["What happens with my data & my customers' data?", "Affra is a software platform — we do not harvest, collect, sell, trade or distribute your or your customers' data.\n\nWe use third-party payment processors and third-party email delivery partners. We cannot access your or your customers' data without your written permission to do so (such as support requests or new features)."],
          ].map(([q, a]) => (
            <div key={q} className="py-6">
              <div className="font-display font-bold text-lg">{q}</div>
              <div className="mt-2 text-zinc-600 text-[15px] whitespace-pre-line">{a}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="rounded-3xl bg-indigo-600 text-white p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -right-12 -bottom-12 w-64 h-64 rounded-full bg-white/10" aria-hidden="true" />
          <h3 className="font-display font-black text-3xl md:text-5xl tracking-tighter max-w-2xl">Your first event, live in 10 minutes.</h3>
          <p className="mt-4 text-indigo-100 max-w-md">No credit card. No lock-in. Just tickets, sold.</p>
          <Link to="/register" className="inline-block mt-8">
            <Button size="lg" className="rounded-full h-12 px-6 bg-white text-zinc-900 hover:bg-zinc-100 text-base font-semibold">
              Create your event <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* CONTACT */}
      <section
        id="contact"
        data-testid="contact-section"
        className="relative bg-white border-t border-zinc-200 py-24 lg:py-28 scroll-mt-24"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="text-xs font-mono text-indigo-600 uppercase tracking-widest">/ contact</div>
              <h2 className="mt-3 font-display font-black text-4xl lg:text-6xl leading-[0.95] tracking-tighter">
                Speak with our founders.
              </h2>
              <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-md">
                No sales teams. No account managers. No handovers. Just direct access to the people who built Affra with love.
              </p>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-1 gap-4">
              {[
                {
                  id: "hello",
                  label: "Say hello",
                  email: "hello@affra.io",
                  blurb: "General questions, product ideas, or just a chat about your event.",
                  accent: "bg-indigo-600",
                  hover: "hover:border-indigo-600",
                },
                {
                  id: "press",
                  label: "Press & partnerships",
                  email: "press@affra.io",
                  blurb: "Media, interviews, podcasts, and collaboration enquiries.",
                  accent: "bg-rose-600",
                  hover: "hover:border-rose-600",
                },
                {
                  id: "support",
                  label: "Customer support",
                  email: "support@affra.io",
                  blurb: "Already a customer? We usually reply within a couple of hours.",
                  accent: "bg-emerald-600",
                  hover: "hover:border-emerald-600",
                },
              ].map((c) => (
                <a
                  key={c.id}
                  href={`mailto:${c.email}`}
                  data-testid={`contact-${c.id}`}
                  className={`group relative rounded-2xl border-2 border-zinc-200 bg-white p-6 lg:p-7 transition ${c.hover} hover:shadow-lg`}
                >
                  <div className="flex items-start gap-5">
                    <div className={`w-11 h-11 rounded-xl ${c.accent} text-white grid place-items-center shrink-0`}>
                      <Mail className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                        {c.label}
                      </div>
                      <div className="mt-1 font-display font-bold text-xl lg:text-2xl text-zinc-900 tracking-tight break-all">
                        {c.email}
                      </div>
                      <p className="mt-2 text-sm text-zinc-500">{c.blurb}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-zinc-300 group-hover:text-zinc-900 group-hover:translate-x-0.5 transition mt-2 shrink-0" />
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between text-sm text-zinc-500 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Logo size="h-24" />
            <span>© {new Date().getFullYear()} — white-label ticketing.</span>
          </div>
          <div className="flex items-center gap-6 flex-wrap">
            <a href="#features" className="hover:text-zinc-900">Features</a>
            <a href="#pricing" className="hover:text-zinc-900">Pricing</a>
            <Link to="/about" className="hover:text-zinc-900" data-testid="footer-about-link">About</Link>
            <a href="#contact" className="hover:text-zinc-900" data-testid="footer-contact-link">Contact</a>
            <Link to="/privacy" className="hover:text-zinc-900" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/terms" className="hover:text-zinc-900" data-testid="footer-terms-link">Terms</Link>
            <Link to="/login" className="hover:text-zinc-900">Log in</Link>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
}
